import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { CellBlockedPredicate } from "../core/LineOfSight.ts";
import { hasLineOfSight } from "../core/LineOfSight.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import { findTarget } from "./Targeting.ts";
import { isStealthHidden } from "./StealthGuard.ts";
import { isTanky } from "./combatUtils.ts";
import type { ApexMod } from "./DamageCalculation.ts";
import { TOWER_CAPABILITY } from "../core/config.ts";

export interface BeamCombatContext {
  enemies: Enemy[];
  towers: Tower[];
  rangeMultiplier: number;
  draftSystem: DraftSystem | null;
  elementBonuses: Record<string, number>;
  globalDamageMult: number;
  apex?: ApexMod | null;
  bossDamageMult: number;
  preferMarked: boolean;
  aoeMark: boolean;
  aoeMarkRadius: number;
  losBlockedAt: CellBlockedPredicate | null;
  bus: EventBus;
  rng: () => number;
}

/** CR-18: Reset + recompute stealth reveal based on signal tower proximity.
 *  Tracks revealedAt timestamp so the renderer can play a transition VFX. */
export function updateStealthReveal(
  ctx: BeamCombatContext,
  time: number,
): void {
  for (const enemy of ctx.enemies) {
    if (!enemy.active || !enemy.stealth) continue;
    const wasRevealed = enemy.revealed;
    enemy.revealed = false;
    for (const tower of ctx.towers) {
      if (!tower.detectStealth) continue;
      const effectiveRange = tower.range * ctx.rangeMultiplier;
      const rangeSq = effectiveRange * effectiveRange;
      const dx = tower.x - enemy.x;
      const dy = tower.y - enemy.y;
      if (dx * dx + dy * dy <= rangeSq) {
        enemy.revealed = true;
        break;
      }
    }
    // Record transition timestamp for VFX
    if (enemy.revealed !== wasRevealed) {
      enemy.revealedAt = time;
    }
  }
}

/** Update a beam (lightning) tower — continuous DPS with lock-on targeting */
export function updateBeamTower(
  tower: Tower,
  dt: number,
  ctx: BeamCombatContext,
): void {
  // BL-175 / D-266 — lightning's GOLD "TEMPEST · Thunderstrike" cadence, ticked
  // OFF the beam (board-wide, range-independent), so it fires even while the beam
  // is between targets. Inert below GOLD / for a non-`goldStrikePeriodMs` beam.
  updateThunderstrike(tower, dt, ctx);

  const effectiveRange = tower.range * ctx.rangeMultiplier;
  const effectiveRangeSq = effectiveRange * effectiveRange;
  const losClear = (enemy: Enemy): boolean =>
    ctx.losBlockedAt == null
      ? true
      : hasLineOfSight(tower.x, tower.y, enemy.x, enemy.y, ctx.losBlockedAt);

  if (tower.beamTarget) {
    const dx = tower.x - tower.beamTarget.x;
    const dy = tower.y - tower.beamTarget.y;
    const outOfRange = dx * dx + dy * dy > effectiveRangeSq;
    if (
      !tower.beamTarget.active ||
      tower.beamTarget.version !== tower.beamTargetVersion ||
      outOfRange ||
      !losClear(tower.beamTarget) ||
      isStealthHidden(tower.beamTarget)
    ) {
      tower.beamTarget = null;
      tower.beamTargetVersion = 0;
      tower.beamActive = false;
      tower.beamDamageAccum = 0;
      tower.beamSurgeTimer = 0;
    }
  }

  if (!tower.beamTarget) {
    tower.beamTarget = findTarget(
      tower,
      ctx.enemies,
      effectiveRange,
      ctx.preferMarked,
      false,
      losClear,
    );
    tower.beamTargetVersion = tower.beamTarget?.version ?? 0;
    tower.beamActive = !!tower.beamTarget;
  }

  if (tower.beamTarget && tower.beamActive) {
    const beamEpicFR = ctx.draftSystem?.getGlobalFireRateMultiplier() ?? 1;
    const dps = tower.damage * tower.fireRate * beamEpicFR;
    tower.beamDPS = dps;
    const frameDamage = dps * (dt / 1000);

    // Apply multipliers without rounding (accumulate fractional damage)
    let multiplier = 1;
    if (ctx.draftSystem) {
      multiplier *=
        ctx.draftSystem.getDamageMultiplier(tower.element) *
        ctx.draftSystem.getGlobalDamageMultiplier();
    }
    const elBonus = ctx.elementBonuses[tower.element];
    if (elBonus !== undefined) multiplier *= elBonus;
    // APEX_PREDATOR: dominant element boosted, others penalized (null = neutral).
    if (ctx.apex) {
      multiplier *=
        tower.element === ctx.apex.dominant ? ctx.apex.boost : ctx.apex.penalty;
    }
    multiplier *= ctx.globalDamageMult;
    // BL-175 / D-266 — signal's GOLD "Command Aura" buff (1 with no ORACLE in
    // range → byte-identical).
    multiplier *= tower.commandAuraMult;

    tower.beamDamageAccum += frameDamage * multiplier;
    if (tower.beamDamageAccum < 1) return;
    let actualDamage = Math.floor(tower.beamDamageAccum);
    tower.beamDamageAccum -= actualDamage;

    // BL-174 / D-263 — lightning's "Overload" surge (level-gated, deterministic):
    // bank the on-target time and, each `surgePeriodMs`, force a GUARANTEED crit
    // — the dependable cadence that turns the pure-RNG burst (§H3) into a steady
    // beat. Below the gate / for a non-`surgePeriodMs` special this never runs and
    // the RNG crit branch is taken EXACTLY as before (byte-identical early game).
    let surgeCrit = false;
    if (
      tower.level >= TOWER_CAPABILITY.UNLOCK_LEVEL &&
      tower.special?.surgePeriodMs !== undefined
    ) {
      tower.beamSurgeTimer += dt;
      if (tower.beamSurgeTimer >= tower.special.surgePeriodMs) {
        tower.beamSurgeTimer -= tower.special.surgePeriodMs;
        surgeCrit = true;
      }
    }

    // Burst crit (EMP special): the guaranteed surge OR the per-tick RNG crit.
    // Short-circuit keeps the RNG draw identical to baseline whenever the surge
    // is inactive (every sub-gate tower) — the determinism rail.
    if (
      surgeCrit ||
      (tower.special?.type === "burst" &&
        ctx.rng() < (tower.special.critChance ?? 0.2))
    ) {
      actualDamage = Math.round(
        actualDamage * (tower.special?.critMultiplier ?? 2),
      );
    }

    // Mark bonus (SIGNAL tower debuff)
    if (tower.beamTarget.marked) {
      actualDamage = Math.round(
        actualDamage * (1 + tower.beamTarget.markDamageBonus),
      );
    }

    // (F-06) The old "unrevealed-stealth beam 50% penalty" lived here. It is
    // unreachable: the lock-on guard above drops beamTarget the moment it is
    // unrevealed stealth (isStealthHidden), so beamTarget is never hidden when
    // damage is applied. Removed to avoid misleading future maintainers.

    // Boss/Tank/Golem damage multiplier (zero_day_exploit / cold_boot)
    if (isTanky(tower.beamTarget)) {
      actualDamage = Math.round(actualDamage * ctx.bossDamageMult);
    }

    const primary = tower.beamTarget;
    const killed = primary.takeDamage(actualDamage, tower.element);

    // BL-182 / D-265 (S169) — lightning's "Burst → Arc" L4/L5 beats. Both ride
    // ONLY the deterministic Overload surge crit (never the RNG crit), so they
    // never draw `rng()` — the surge is a dt timer, byte-safe + sub-gate-inert.
    //   • L5 "Chain-Storm" (`chainStormRadius`): the surge crit arcs to EVERY foe
    //     within the radius (a mini-AoE on the guaranteed crit) — supersedes Fork.
    //   • L4 "Fork" (`forkFraction`): the surge crit also forks to the single
    //     nearest other foe at a reduced fraction.
    if (surgeCrit) {
      arcSurge(tower, primary, actualDamage, effectiveRangeSq, ctx);
    }

    if (killed) {
      primary.active = false;
      tower.beamTarget = null;
      tower.beamTargetVersion = 0;
      tower.beamActive = false;
      tower.beamDamageAccum = 0;
      tower.beamSurgeTimer = 0;
    }

    tower.updateCooldown(dt);
    if (tower.canFire()) {
      ctx.bus.emit("tower.fired", { tower, projectile: null });
      tower.fire();
    }
  }

  tower.target = tower.beamTarget;
}

/** BL-182 / D-265 (S169) — lightning's "Burst → Arc" L4/L5 surge spread. Called
 *  ONLY on a guaranteed Overload surge crit, so it is wholly deterministic (no
 *  `rng()`) and inert below the gate / for any non-`surgePeriodMs` beam. The arc
 *  damage is a fraction of the surge hit; arc kills route through the same death
 *  scan as the primary. The two beats are mutually exclusive by gate:
 *   • L5 "Chain-Storm" (`chainStormRadius`, gate L5): hits EVERY live foe within
 *     the radius of the primary — a mini-AoE on the guaranteed crit.
 *   • L4 "Fork" (`forkFraction`, gate L4): forks to the single nearest other foe. */
function arcSurge(
  tower: Tower,
  primary: Enemy,
  surgeDamage: number,
  beamRangeSq: number,
  ctx: BeamCombatContext,
): void {
  const s = tower.special;
  if (!s) return;
  const fraction = s.forkFraction ?? 0.5;
  const arcDmg = Math.round(surgeDamage * fraction);
  if (arcDmg <= 0) return;

  const hit = (enemy: Enemy): void => {
    const killed = enemy.takeDamage(arcDmg, tower.element);
    ctx.bus.emit("projectile.hit", {
      projectile: { x: enemy.x, y: enemy.y, element: tower.element, color: tower.color },
      target: enemy,
      damage: arcDmg,
    });
    if (killed) enemy.active = false;
  };

  // L5 "Chain-Storm" — every foe in radius (gate L5: UNLOCK_LEVEL + 2).
  if (
    tower.level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 2 &&
    s.chainStormRadius !== undefined
  ) {
    const rSq = s.chainStormRadius * s.chainStormRadius;
    for (const enemy of ctx.enemies) {
      if (!enemy.active || enemy === primary) continue;
      if (isStealthHidden(enemy)) continue;
      const dx = enemy.x - primary.x;
      const dy = enemy.y - primary.y;
      if (dx * dx + dy * dy <= rSq) hit(enemy);
    }
    return;
  }

  // L4 "Fork" — the single nearest other foe within beam range (gate L4).
  if (
    tower.level >= TOWER_CAPABILITY.UNLOCK_LEVEL + 1 &&
    s.forkFraction !== undefined
  ) {
    let nearest: Enemy | null = null;
    let nearestDistSq = Infinity;
    for (const enemy of ctx.enemies) {
      if (!enemy.active || enemy === primary) continue;
      if (isStealthHidden(enemy)) continue;
      const dx = enemy.x - primary.x;
      const dy = enemy.y - primary.y;
      const distSq = dx * dx + dy * dy;
      if (distSq <= beamRangeSq && distSq < nearestDistSq) {
        nearest = enemy;
        nearestDistSq = distSq;
      }
    }
    if (nearest) hit(nearest);
  }
}

/** BL-175 / D-266 (S170) — lightning's GOLD signature "TEMPEST · Thunderstrike".
 *  On its cadence (`goldStrikePeriodMs`, banked in `tower.goldTimer`) the GOLD
 *  lightning tower calls down `goldStrikeCount` sky-bolts onto the HIGHEST-hp foes
 *  across the WHOLE board (off-beam, range-independent), each for
 *  `goldStrikeDamage`. Brand-new off-beam strikes (NOT the Overload surge).
 *  Wholly deterministic: a dt timer + a stable value-sort, no `rng()`. Gated on
 *  `tower.isGold()` + the data so it never ticks below GOLD (`goldTimer` stays 0)
 *  → no surface in any fixture, byte-identical. */
function updateThunderstrike(
  tower: Tower,
  dt: number,
  ctx: BeamCombatContext,
): void {
  const s = tower.special;
  if (
    !tower.isGold() ||
    !s ||
    s.goldStrikePeriodMs === undefined ||
    s.goldStrikeCount === undefined ||
    s.goldStrikeDamage === undefined
  ) {
    return;
  }
  tower.goldTimer += dt;
  if (tower.goldTimer < s.goldStrikePeriodMs) return;
  tower.goldTimer -= s.goldStrikePeriodMs;
  tower.goldPulseT = 320; // render-only apex flash

  // The strikeable set: live, revealed foes. Sort by HP desc with a STABLE
  // tie-break (the spawn-monotonic `version`) so the choice is order-independent
  // and fully deterministic across runs.
  const candidates: Enemy[] = [];
  for (const enemy of ctx.enemies) {
    if (!enemy.active || isStealthHidden(enemy)) continue;
    candidates.push(enemy);
  }
  candidates.sort((a, b) => b.hp - a.hp || a.version - b.version);

  const n = Math.min(s.goldStrikeCount, candidates.length);
  for (let i = 0; i < n; i++) {
    const enemy = candidates[i]!;
    const killed = enemy.takeDamage(s.goldStrikeDamage, tower.element);
    ctx.bus.emit("projectile.hit", {
      projectile: {
        x: enemy.x,
        y: enemy.y,
        element: tower.element,
        color: tower.color,
      },
      target: enemy,
      damage: s.goldStrikeDamage,
    });
    if (killed) enemy.active = false;
  }
}

/** Update a SIGNAL tower — applies mark, slow, and honeypot detour */
export function updateSignalTower(
  tower: Tower,
  dt: number,
  ctx: BeamCombatContext,
): void {
  const epicFireRate = ctx.draftSystem?.getGlobalFireRateMultiplier() ?? 1;
  tower.updateCooldown(dt * epicFireRate);
  const effectiveRange = tower.range * ctx.rangeMultiplier;
  const losClear = (enemy: Enemy): boolean =>
    ctx.losBlockedAt == null
      ? true
      : hasLineOfSight(tower.x, tower.y, enemy.x, enemy.y, ctx.losBlockedAt);
  tower.target = findTarget(
    tower,
    ctx.enemies,
    effectiveRange,
    false,
    false,
    losClear,
  );

  if (tower.target && tower.canFire()) {
    const enemy = tower.target;
    // Apply mark (non-stacking: SET, not ADD)
    enemy.marked = true;
    enemy.markDamageBonus = tower.special?.markDamageBonus ?? 0.2;
    enemy.markTimer = tower.special?.markDuration ?? 3000;
    // Apply slow via the timer-backed API so it persists across frames
    if (tower.special?.slowFactor) {
      enemy.applySlow(
        tower.special.slowFactor,
        tower.special?.markDuration ?? 3000,
      );
    }
    // Honeypot: T3+ SIGNAL tower inserts detour waypoint
    if (tower.level >= 3) {
      enemy.insertDetour({ x: tower.x, y: tower.y });
    }
    tower.fire();
    ctx.bus.emit("tower.fired", { tower, projectile: null });

    // Broadcast synergy: spread mark to nearby enemies
    if (ctx.aoeMark) {
      const aoeRadiusSq = ctx.aoeMarkRadius * ctx.aoeMarkRadius;
      for (const e of ctx.enemies) {
        if (!e.active || e === enemy) continue;
        // F-07: never spread marks onto unrevealed stealth — a mark would
        // betray the hidden enemy's presence to the targeting layer.
        if (isStealthHidden(e)) continue;
        const dx = e.x - enemy.x;
        const dy = e.y - enemy.y;
        if (dx * dx + dy * dy <= aoeRadiusSq) {
          e.marked = true;
          e.markDamageBonus = enemy.markDamageBonus;
          e.markTimer = enemy.markTimer;
        }
      }
    }
  }
}
